import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { useGetStarted } from "@/components/GetStartedDialog";
import { sanitizeHtml } from "@/lib/sanitize";

const process = [
  {
    step: "01",
    title: "Discovery",
    body: "We start with a conversation. What does the business actually need? Who is it for? What does success look like six months from now?",
  },
  {
    step: "02",
    title: "Strategy & structure",
    body: "Sitemap, content model, and page flows — mapped before pixel one. Design decisions get easier when the structure is right.",
  },
  {
    step: "03",
    title: "Design in the browser",
    body: "I design in code from day one. That means what you approve is what you get — no surprises at handoff.",
  },
  {
    step: "04",
    title: "Build & polish",
    body: "Custom-coded, performance-tuned, accessibility-checked. Small details are where good sites become great ones.",
  },
  {
    step: "05",
    title: "Launch & beyond",
    body: "I deploy, hand over the keys, and stick around. Sites should grow with the businesses that run them.",
  },
];

const services = [
  { name: "Marketing sites", note: "Brand-forward, conversion-minded." },
  { name: "E-commerce", note: "Custom Shopify, Stripe, headless commerce." },
  { name: "Editorial platforms", note: "For publishers, writers, and studios." },
  { name: "Web apps", note: "Custom tools built for real workflows." },
  { name: "Redesigns", note: "Rebuilds of tired or template-locked sites." },
  { name: "Ongoing care", note: "Retainers for the businesses I ship for." },
];

export default function About() {
  const { open } = useGetStarted();
  return (
    <div data-testid="about-page">
      <PageHead
        title="About Redkey Web Design | Senior Web Developer — Inland Empire & Orange County"
        description="Meet Redkey Web Design: a senior developer with 15+ years building custom, accessible websites for Southern California governments, practices, and businesses."
        canonical="https://redkeyweb.design/about"
      />
      {/* Intro */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
        <p className="eyebrow">03 / About</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-6">
          <h1
            data-testid="about-headline"
            className="md:col-span-8 font-display text-5xl md:text-7xl leading-[1.02]"
          >
            Redkey Web Design — for businesses who&rsquo;d rather{" "}
            <em className="not-italic text-brand-red">stand out</em> than fit in.
          </h1>
          <div className="md:col-span-4 md:pt-4">
            <p className="text-ink-soft leading-relaxed">
              Redkey is an independent design and development studio building
              considered, custom websites end-to-end. No agencies, no
              hand-offs, no template lock-in — just direct collaboration and
              carefully written code.
            </p>
          </div>
        </div>
      </section>

      {/* Portrait */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6" data-testid="founder-portrait">
            <div className="aspect-[4/5] overflow-hidden bg-paper-alt border hairline">
              <img
                src="/brand/founder-1.jpg"
                alt="Founder of Redkey Web Design"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 20%" }}
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="eyebrow">Founder</p>
            <h2 className="font-display text-4xl md:text-6xl mt-4">
              Hi — I&rsquo;m the person who&rsquo;ll actually build your site.
            </h2>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-prose">
              At Redkey Web Design, there&rsquo;s no account manager funneling
              your notes through three teams. You talk to me. I sketch,
              design, code, and ship — end to end. That&rsquo;s not a
              marketing story; it&rsquo;s the whole operating model.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6 max-w-md">
              <div>
                <p className="eyebrow">Working</p>
                <p className="mt-2 font-ui">Remote · Nationwide</p>
              </div>
              <div>
                <p className="eyebrow">Experience</p>
                <p className="mt-2 font-ui">15+ years shipping</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Long-form bio */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow">Bio</p>
            <div className="hidden md:block mt-6 aspect-[3/4] overflow-hidden bg-paper-alt border hairline" data-testid="founder-portrait-secondary">
              <img
                src="/brand/founder-2.jpg"
                alt="Founder of Redkey Web Design"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 25%" }}
              />
            </div>
          </div>
          <div className="md:col-span-8 space-y-6 text-lg leading-relaxed text-ink-soft max-w-prose">
            <p>
              Redkey Web Design is a premium U.S. web design and development
              studio led by a senior developer with{" "}
              <strong className="text-ink font-ui">15+ years</strong> of
              professional experience — including enterprise Drupal builds,
              multi-department government websites, ADDY Award-winning
              municipal media platforms, and small-business sites that
              actually rank and convert.
            </p>
            <p>
              I started this practice because most agencies were building
              websites the wrong way round — a template first, a business
              squeezed in afterwards. Then a junior designer shipped it, an
              account manager relayed the notes, and the client wondered why
              nothing on the page reflected their business. That model is
              backwards. So I inverted it.
            </p>
            <p>
              At Redkey Web Design you work directly with the senior developer
              writing the code. No hand-offs. No junior-to-senior escalation.
              No account managers filtering your notes. Every decision — from
              content strategy through accessibility, performance,
              SEO, and long-term maintenance — is made and executed by one
              experienced developer who is accountable for the outcome.
            </p>
            <p>
              We specialize in{" "}
              <strong className="text-ink font-ui">custom web design</strong>,{" "}
              <strong className="text-ink font-ui">Drupal development</strong>,{" "}
              <strong className="text-ink font-ui">technical SEO</strong>,{" "}
              <strong className="text-ink font-ui">WCAG 2.2 accessibility</strong>, and{" "}
              <strong className="text-ink font-ui">AI-powered workflow automation</strong>{" "}
              for businesses, organizations, and government agencies across
              the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t hairline bg-paper-alt">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="eyebrow">Services</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-6 leading-[1.02]">
              What I make.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 mt-14">
            {services.map((s) => (
              <div key={s.name} className="border-t hairline pt-6">
                <h3 className="font-display text-2xl tracking-tight">{s.name}</h3>
                <p className="mt-2 text-ink-soft">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
            <div>
              <p className="eyebrow">Process</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-6">
                Five steps, done properly.
              </h2>
            </div>
          </div>

          <div className="space-y-0">
            {process.map((p) => (
              <div
                key={p.step}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-8 border-t hairline items-start"
              >
                <div className="md:col-span-2 font-mono-ui text-sm text-muted-ink tracking-widest">
                  {p.step}
                </div>
                <h3 className="md:col-span-4 font-display text-2xl md:text-3xl tracking-tight">
                  {p.title}
                </h3>
                <p className="md:col-span-6 text-ink-soft text-lg leading-relaxed max-w-prose">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-t hairline bg-ink text-paper">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-20 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="font-mono-ui text-[11px] tracking-widest text-paper/50">Principles</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-6 leading-[1.02] text-paper">
              A few things I believe.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8 md:pt-4">
            {[
              ["Craft first", "Design is not decoration. It&rsquo;s how the thing works."],
              ["Small teams win", "One thoughtful builder beats a committee of drag-and-drop."],
              ["Slow is smooth", "Careful decisions ship fewer bugs and better sites."],
              ["Say honestly", "If a template would serve you better, I&rsquo;ll say so."],
            ].map(([t, b]) => (
              <div key={t}>
                <p className="text-electric font-display text-xl">— {t}</p>
                <p className="mt-2 text-paper/75" dangerouslySetInnerHTML={{ __html: sanitizeHtml(b) }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-display text-3xl md:text-4xl max-w-[24ch]">
            Ready to build something worth the domain?
          </p>
          <div className="flex flex-wrap gap-3">
          <Link
            to="/work"
            data-testid="about-cta"
            className="inline-flex items-center gap-3 border hairline-strong px-6 py-4 hover:border-ink transition-colors duration-200 font-ui"
          >
            See selected work <span aria-hidden>→</span>
          </Link>
          <button
            type="button"
            onClick={open}
            data-testid="about-get-started"
            className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui"
          >
            Get Started <span aria-hidden>→</span>
          </button>
          </div>
        </div>
      </section>
    </div>
  );
}
